import Papa from 'papaparse';
import { dateFromTimestamp } from './util';

/**
 * A container for sorting and accessing events by date, group and student.
 * 
 * Expects event objects to have the following structure:
 * 
 *  {
 *      id: String|Number,
 *      date: Timestamp,
 *      students: Array,
 *      groups: Array,
 *  }
 * 
 * Expects background events to have:
 * 
 *  {
 *      datefrom: Timestamp,
 *      dateto: Timestamp,
 *      groups: Array,
 *  }
 */
class EventContainer {
    eventsByGroup = {}
    eventsByStudent = {}
    eventsById = {}

    // We'll use this to generate background colours for BG events
    rgb = [0, 100, 255]

    constructor(events=[], bgEvents=[]) {
        events.forEach((event) => {
            this.insertEvent(event)
        })
        bgEvents.forEach((event) => {
            this.insertBgEvent(event)
        })

        if (bgEvents.length) {
            // Let's sort each set of background events by 'day' to ensure consistent rendering
            Object.values(this.eventsByGroup).forEach(date => {
                Object.keys(date).forEach(group => {
                    date[group].sort((a, b) => a.day < b.day ? 1 : -1)
                }) 
            })
        }
    }

    /**
     * @param {string|integer} year E.g. 1999
     * @param {string|integer} month 0-11
     * @param {string|integer} date 1-31
     */
    static getKey(year, month, date) {
        // Let's pad our single-digit months and days so that our keys are sortable.
        return `${Number(year)}-${Number(month) < 9 ? '0' : ''}${Number(month) + 1}-${Number(date) < 10 ? '0' : ''}${Number(date)}`
    }

    /**
     * Sorts events into two dictionaries that use date -> group ID, date -> student ID respectively. 
     * 
     * If no groups/users associated, ID defaults to -1: We can refer to this to retrieve events without user / group data attached.
     * 
     * @param {object} event 
     */
    insertEvent(event) {
        this.eventsById[event.id] = event

        const date = dateFromTimestamp(event.date)
        const key = EventContainer.getKey(date.getFullYear(), date.getMonth(), date.getDate())
    
        this.insertByStudents(event, key)
        this.insertByGroups(event, key)
    }

    /**
     * Only inserts by students. See insertEvent()
     * 
     * @param {object} event 
     * @param {string} key Pass if key is already calculated
     * @param {string|number} userId Pass to force insertion to a particular user.
     */
    insertByStudents(event, key=null, userId=null) {
        if (!key) {
            const date = dateFromTimestamp(event.date)
            key = EventContainer.getKey(date.getFullYear(), date.getMonth(), date.getDate())
        }

        if (!this.eventsByStudent[key]) this.eventsByStudent[key] = {}

        if (!userId && event.students && event.students.length) event.students.forEach(user => {
            if (!this.eventsByStudent[key][user.id]) this.eventsByStudent[key][user.id] = []
            if (this.eventsByStudent[key][user.id].findIndex(e => e.id === event.id) > -1) return
            this.eventsByStudent[key][user.id].push(event)
        }) 
        else {
            if (userId === null) userId = -1

            if (!this.eventsByStudent[key][userId]) this.eventsByStudent[key][userId] = []
            if (this.eventsByStudent[key][userId].findIndex(e => e.id === event.id) > -1) return
            this.eventsByStudent[key][userId].push(event)
        }
    }


    /**
     * Only inserts by students. See insertEvent()
     * 
     * @param {object} event 
     * @param {string} key Pass if key is already calculated
     */
    insertByGroups(event, key=null) {
        if (!key) {
            const date = dateFromTimestamp(event.date)
            key = EventContainer.getKey(date.getFullYear(), date.getMonth(), date.getDate())
        }
        
        if (!this.eventsByGroup[key]) this.eventsByGroup[key] = {}

        if (event.groups && event.groups.length) event.groups.forEach(group => {
            if (!this.eventsByGroup[key][group.id]) this.eventsByGroup[key][group.id] = []
            if (this.eventsByGroup[key][group.id].findIndex(e => e.id === event.id) > -1) return
            this.eventsByGroup[key][group.id].push(event)
        })
        else {
            if (!this.eventsByGroup[key][-1]) this.eventsByGroup[key][-1] = []
            if (this.eventsByGroup[key][-1].findIndex(e => e.id === event.id) > -1) return
            this.eventsByGroup[key][-1].push(event)
        }
    }

    insertBgEvent(bgEvent) {
        const datefrom = dateFromTimestamp(bgEvent.datefrom)
        const dateto = dateFromTimestamp(bgEvent.dateto)

        let eventDay = 1
        let key

        for (let date = new Date(datefrom); date <= dateto; date.setDate(date.getDate() + 1)) {
            key = EventContainer.getKey(date.getFullYear(), date.getMonth(), date.getDate())

            if (!this.eventsByGroup[key]) this.eventsByGroup[key] = {}

            if (bgEvent.groups && bgEvent.groups.length) {
                bgEvent.groups.forEach(group => {
                    if (!this.eventsByGroup[key][group.id]) this.eventsByGroup[key][group.id] = []
                    this.eventsByGroup[key][group.id].push({
                        day: eventDay,
                        rgb: [...this.rgb],
                        ...bgEvent,
                    })
                })
            } else {
                if (!this.eventsByGroup[key][-1]) this.eventsByGroup[key][-1] = []
                this.eventsByGroup[key][-1].push({
                    day: eventDay,
                    rgb: [...this.rgb],
                    ...bgEvent,
                })
            }

            eventDay++
        }

        this.rgb[0] = (this.rgb[0] + 11) % 128
        this.rgb[1] = ((this.rgb[1] + 11) % 128) + 64
        this.rgb[2] = ((this.rgb[2] + 11) % 128) + 128
    }

    /**
     * @param {integer} year E.g. 1999
     * @param {integer} month 0-11
     * @param {integer} date 1-31
     * @param {integer|string|undefined} groupId If not specified, returns all events on the date, indexed by group ID
     */
    getGroupEvents(year, month, date, groupId=null) {
        const key = EventContainer.getKey(year, month, date)

        if (!this.eventsByGroup[key]) return {}
        if (!groupId) return this.eventsByGroup[key]

        if (!this.eventsByGroup[key][groupId]) return {}
        return this.eventsByGroup[key][groupId].sort((a, b) => a.from < b.from ? -1 : 1)
    }

    /**
     * @param {integer|string} year E.g. 1999
     * @param {integer|string} month 0-11
     * @param {integer|string} date 1-31
     * @param {integer|string|undefined} userId If not specified, returns all events on the date, indexed by user ID
     */
    getStudentEvents(year, month, date, userId=null) {
        const key = EventContainer.getKey(year, month, date)

        if (!this.eventsByStudent[key]) return userId ? [] : {}
        if (!userId) return this.eventsByStudent[key]
        
        if (!this.eventsByStudent[key][userId]) return []
        return this.eventsByStudent[key][userId] 
    }

    /**
     * Get all students whose date and events pass the tests given.
     * 
     * @param {function} keyCb Filter applied to the YYYY-MM-DD date keys.
     * @param {function} eventsCb Filter applied to the student event array, for each day that passed the first callback.
     * @returns {Array} An array of user IDs
     */
    getStudentsWhere(keyCb, eventsCb) {
        const ids = []
        
        Object.keys(this.eventsByStudent).filter(keyCb).forEach(date => {
            Object.keys(this.eventsByStudent[date]).forEach(id => {
                if (eventsCb(this.eventsByStudent[date][id])) ids.push(id)
            })
        })

        return ids
    }

    /**
     * Check at least one event exists which passes the callback tests given.
     * 
     * @param {function} keyCb Filter applied to the YYYY-MM-DD date keys.
     * @param {function} idCb Filter applied to the user ID keys.
     * @param {function} eventCb Filter applied to the user event array, for each day that passed the first callback.
     * 
     * @returns {boolean} True if an event was found which passes the tests
     */
    checkExists(keyCb, idCb, eventCb=null) {
        for (const key of Object.keys(this.eventsByStudent).filter(keyCb)) {
            for (const id of Object.keys(this.eventsByStudent[key])) {
                if (!idCb(id)) continue
                if (!eventCb) return true

                for (const event of this.eventsByStudent[key][id]) {
                    if (eventCb(event)) return true
                }
            }
        }
        
        return false
    }

    /**
     * Remove any event which passes the callback tests given.
     * 
     * @param {function} keyCb Filter applied to the YYYY-MM-DD date keys.
     * @param {function} idCb Filter applied to the user ID keys.
     * @param {function} eventCb Filter applied to the user event array, for each day that passed the first callback.
     * 
     */
    removeWhere(keyCb, idCb, eventCb=null) {
        for (const key of Object.keys(this.eventsByStudent).filter(keyCb)) {
            for (const id of Object.keys(this.eventsByStudent[key])) {
                if (!idCb(id)) continue
                if (!eventCb) {
                    delete this.eventsByStudent[key][id]
                    if (!Object.keys(this.eventsByStudent[key]).length) delete this.eventsByStudent[key]
                    continue
                }
                for (let i = this.eventsByStudent[key][id].length-1; i >= 0; --i) {
                    if (eventCb(this.eventsByStudent[key][id][i])) this.eventsByStudent[key][id].splice(i, 1)
                }
                if (this.eventsByStudent[key][id].length === 0) delete this.eventsByStudent[key][id]
            }
        }
    }

    allEvents() {
        return Object.values(this.eventsById)
    }

    /**
     * Returns a count of all user IDs stored.
     * 
     * @returns {integer} The total count.
     */
    countStudents() {
        const found = {}
        let count = 0

        Object.keys(this.eventsByStudent).forEach(key => {
            Object.keys(this.eventsByStudent[key]).forEach(id => {
                if (found[id]) return
                found[id] = true
                ++count
            })
        })

        return count
    }

    /**
     * Clears keys within the range [from, to]. Keys are YYYY-MM-DD date strings, so this is equivalent to clearing events within a date range.
     * 
     * @param {string|null} from YYYY-MM-DD. If null, clears all events up to and including 'to'.
     * @param {string|null} to YYYY-MM-DD. If null, clears all events from 'from' onwards. 
     */
    clearRange(from=null, to=null) {
        if (!(from || to)) {
            this.eventsByGroup = {}
            this.eventsByStudent = {}
            return
        }
        if (!from) {
            Object.keys(this.eventsByGroup).forEach(k => {
                if (k <= to) delete this.eventsByGroup[k]
            })
            Object.keys(this.eventsByStudent).forEach(k => {
                if (k <= to) delete this.eventsByStudent[k]
            })
            return
        }
        if (!to) {
            Object.keys(this.eventsByGroup).forEach(k => {
                if (k >= from) delete this.eventsByGroup[k]
            })
            Object.keys(this.eventsByStudent).forEach(k => {
                if (k >= from) delete this.eventsByStudent[k]
            })
            return
        }
        Object.keys(this.eventsByGroup).forEach(k => {
            if (from <= k && k <= to) delete this.eventsByGroup[k]
        })
        Object.keys(this.eventsByStudent).forEach(k => {
            if (from <= k && k <= to) delete this.eventsByStudent[k]
        })
    }

    /**
     * @param {integer} year E.g. 1999
     * @param {integer} month 0-11
     * @param {integer} date 1-31
     */
    isEmpty(year, month, date) {
        return !this.eventsByStudent[EventContainer.getKey(year, month, date)]
    }

    /**
     * Returns a CSV string of users to rooms by date. Only acknowledges first event of the day for each student.
     * 
     * @param {Array} users An array of users to map to the CSV. 
     * 
     * All users require an 'id' key, and by default userFields expects 'displayname'.
     * 
     * @param {Object} fields Key-value pairs of the column name and associated field name of user info to be included in each row
     * @param {Function} eventsCb The callback to be applied to the events for a given user on a given date. Must return a string.
     */
    getCSV(users, userFields={'Name': 'displayname'}, eventsCb=(events) => {
        return (events[0] && events[0].ttroom && events[0].ttroom.name) 
        ? events[0].ttroom.name : null
    }) {
        const dates = Object.keys(this.eventsByStudent).sort((a, b) => a > b ? 1 : -1)

        const fields = [Object.keys(userFields), ...dates]
        const data = []

        users.forEach(user => {
            let row = Object.values(userFields).map(field => user[field])

            dates.forEach(key => {
                if (!this.eventsByStudent[key] || !this.eventsByStudent[key][user.id]) {
                    row.push(null)
                    return
                }

                row.push(eventsCb(this.eventsByStudent[key][user.id]))
            })

            data.push(row)
        })

        return Papa.unparse({ fields, data })
    }
}

export default EventContainer